import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUser } from '../context/UserContext';
import { apiCall } from '../api/client';
import logo from '../components/BeeFluent logos/beefluent_logo_favicon_192.png';

export default function Settings() {
  const { token, logout } = useAuth();
  // Already loaded at sign-in (App gates on it), so the form initializes directly.
  const { user, setUser } = useUser();
  const navigate = useNavigate();
  const onBack = () => navigate('/');
  const [username, setUsername] = useState(user?.username || '');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [learnSize, setLearnSize] = useState(String(user?.settings?.learnSessionSize ?? 6));
  const [reviewSize, setReviewSize] = useState(String(user?.settings?.reviewSessionSize ?? 20));
  const [prefsError, setPrefsError] = useState('');
  const [prefsSuccess, setPrefsSuccess] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deletePublic, setDeletePublic] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Push a PATCH /me response into the shared user, keeping computed fields
  // (hiveState, admin) from the previous value — the PATCH response carries the
  // stored user doc, not the computed view that GET /me returns.
  function applyUpdate(updated) {
    setUser(prev => ({ ...prev, username: updated.username, settings: updated.settings }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setSaving(true);
    try {
      const updated = await apiCall('PATCH', '/me', { username }, token);
      applyUpdate(updated);
      setUsername(updated.username || '');
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSavePrefs(e) {
    e.preventDefault();
    setPrefsError('');
    setPrefsSuccess(false);

    const learn = parseInt(learnSize, 10);
    const review = parseInt(reviewSize, 10);
    if (!Number.isInteger(learn) || learn < 1 || learn > 50) {
      setPrefsError('Words per learn session must be between 1 and 50.');
      return;
    }
    if (!Number.isInteger(review) || review < 1 || review > 100) {
      setPrefsError('Words per review session must be between 1 and 100.');
      return;
    }

    setSavingPrefs(true);
    try {
      const updated = await apiCall('PATCH', '/me', {
        settings: { learnSessionSize: learn, reviewSessionSize: review }
      }, token);
      applyUpdate(updated);
      setLearnSize(String(updated.settings?.learnSessionSize ?? learn));
      setReviewSize(String(updated.settings?.reviewSessionSize ?? review));
      setPrefsSuccess(true);
    } catch (err) {
      setPrefsError(err.message);
    } finally {
      setSavingPrefs(false);
    }
  }

  async function handleDelete() {
    setDeleteError('');
    setDeleting(true);
    try {
      await apiCall('DELETE', '/me', { deletePublishedCourses: deletePublic }, token);
      // Account is gone — end the session. logout() redirects away from the app.
      logout();
    } catch (err) {
      setDeleteError(err.message);
      setDeleting(false);
    }
  }

  const unchanged = !user || username.trim() === (user.username || '');
  const prefsUnchanged = !user ||
    (learnSize === String(user.settings?.learnSessionSize ?? 6) &&
     reviewSize === String(user.settings?.reviewSessionSize ?? 20));

  return (
    <div className="min-h-screen bg-amber-50">
      {/* Header */}
      <header className="bg-white border-b border-amber-100 px-4 sm:px-6 py-4 flex items-center gap-3 sticky top-0 z-10">
        <button
          onClick={onBack}
          className="text-amber-600 hover:text-amber-800 text-sm font-medium whitespace-nowrap"
        >
          ← Back
        </button>
        <h1 className="text-xl sm:text-2xl font-bold text-amber-600 whitespace-nowrap flex items-center gap-0.5">
          <img src={logo} alt="BeeFluent logo" className="h-9 sm:h-10 w-auto" />
          Settings
        </h1>
      </header>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="space-y-6">
            {/* Account section */}
            <section className="bg-white rounded-xl shadow-sm border border-amber-100 p-5 sm:p-6">
              <h2 className="text-lg font-bold text-amber-800 mb-4">Account</h2>

              {user?.email && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-amber-800 mb-1">Email</label>
                  <p className="text-amber-600 text-sm">{user.email}</p>
                </div>
              )}

              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-amber-800 mb-1">Username</label>
                  <input
                    type="text"
                    value={username}
                    onChange={e => { setUsername(e.target.value); setSuccess(false); }}
                    className="w-full border border-amber-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    minLength={2}
                    maxLength={30}
                    required
                  />
                </div>

                {error && <p className="text-red-500 text-sm">{error}</p>}
                {success && <p className="text-green-600 text-sm">Saved!</p>}

                <button
                  type="submit"
                  disabled={saving || unchanged}
                  className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-5 py-2 rounded-lg transition disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save changes'}
                </button>
              </form>
            </section>

            {/* Study preferences section */}
            <section className="bg-white rounded-xl shadow-sm border border-amber-100 p-5 sm:p-6">
              <h2 className="text-lg font-bold text-amber-800 mb-1">Study preferences</h2>
              <p className="text-amber-600 text-sm mb-4">Choose how many words each session covers.</p>

              <form onSubmit={handleSavePrefs} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-amber-800 mb-1">Words per learn session</label>
                  <input
                    type="number"
                    value={learnSize}
                    onChange={e => { setLearnSize(e.target.value); setPrefsSuccess(false); }}
                    className="w-full border border-amber-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    min={1}
                    max={50}
                    required
                  />
                  <p className="text-amber-400 text-xs mt-1">New words introduced in each learn session (1–50).</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-amber-800 mb-1">Words per review session</label>
                  <input
                    type="number"
                    value={reviewSize}
                    onChange={e => { setReviewSize(e.target.value); setPrefsSuccess(false); }}
                    className="w-full border border-amber-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    min={1}
                    max={100}
                    required
                  />
                  <p className="text-amber-400 text-xs mt-1">Words reviewed in each review session (1–100).</p>
                </div>

                {prefsError && <p className="text-red-500 text-sm">{prefsError}</p>}
                {prefsSuccess && <p className="text-green-600 text-sm">Saved!</p>}

                <button
                  type="submit"
                  disabled={savingPrefs || prefsUnchanged}
                  className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-5 py-2 rounded-lg transition disabled:opacity-50"
                >
                  {savingPrefs ? 'Saving...' : 'Save changes'}
                </button>
              </form>
            </section>

            {/* Danger / session section */}
            <section className="bg-white rounded-xl shadow-sm border border-amber-100 p-5 sm:p-6">
              <h2 className="text-lg font-bold text-amber-800 mb-1">Session</h2>
              <p className="text-amber-600 text-sm mb-4">Log out of your BeeFluent account on this device.</p>
              <button
                onClick={logout}
                className="border border-amber-300 text-amber-700 hover:bg-amber-50 font-medium px-5 py-2 rounded-lg transition"
              >
                Log out
              </button>
            </section>

            {/* Danger zone */}
            <section className="bg-white rounded-xl shadow-sm border border-red-200 p-5 sm:p-6">
              <h2 className="text-lg font-bold text-red-600 mb-1">Delete account</h2>
              <p className="text-amber-600 text-sm mb-4">
                Permanently deletes your account and study progress. Your unpublished courses are
                deleted. Published courses stay available to other learners (shown as
                "(Account Deleted)") unless you choose to delete them too.
              </p>

              {!confirmingDelete ? (
                <button
                  onClick={() => { setConfirmingDelete(true); setDeleteError(''); }}
                  className="border border-red-300 text-red-600 hover:bg-red-50 font-medium px-5 py-2 rounded-lg transition"
                >
                  Delete account
                </button>
              ) : (
                <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                  <p className="text-red-700 text-sm font-medium mb-3">
                    This cannot be undone. Are you sure?
                  </p>

                  <label className="flex items-start gap-2 mb-4 text-sm text-amber-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={deletePublic}
                      onChange={e => setDeletePublic(e.target.checked)}
                      className="mt-0.5 accent-red-500"
                    />
                    <span>Also delete my published courses and everyone's progress on them.</span>
                  </label>

                  {deleteError && <p className="text-red-600 text-sm mb-3">{deleteError}</p>}

                  <div className="flex gap-3">
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-2 rounded-lg transition disabled:opacity-50"
                    >
                      {deleting ? 'Deleting...' : 'Permanently delete'}
                    </button>
                    <button
                      onClick={() => { setConfirmingDelete(false); setDeletePublic(false); setDeleteError(''); }}
                      disabled={deleting}
                      className="border border-amber-300 text-amber-700 hover:bg-amber-50 font-medium px-5 py-2 rounded-lg transition disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </section>
        </div>
      </div>
    </div>
  );
}
